import React, { useState } from 'react';
import { Form, Spinner, Alert } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {
  IconUser,
  IconBuildingStore,
  IconCheck,
  IconShieldCheck,
  IconSparkles,
  IconCircleCheck,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import { signupFinalize } from '../../Users/services/UserServicesV2';
import ModalComponent from '../../../jsx/components/Modal';
import NewTermsOfUse from '../../../jsx/components/ModalData/NewTermsOfUse';
import TurnstileWidget from '../../../components/TurnstileWidget';
import { useTurnstile } from '../../../hooks/useTurnstile';

const PaidRegisterForm = ({ planConfig, paymentResult, onRegisterSuccess }) => {
  const [processing, setProcessing] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  const { config: turnstileConfig, shouldShow: shouldShowTurnstile } = useTurnstile();
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const [turnstileResetTrigger, setTurnstileResetTrigger] = useState(0);

  const personType = paymentResult?.person_type ?? 2;
  const isPJ = personType === 1;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      // PF: usamos o customer_name do step anterior; PJ: company_name = razão social (já no customer_name) + first_name novo
      company_name: paymentResult?.customer_name || '',
      first_name: '',
      terms_accepted: false,
    },
  });

  const onSubmit = async (data) => {
    if (shouldShowTurnstile && turnstileConfig?.enabled && !turnstileToken) {
      Swal.fire({ icon: 'warning', title: 'Verificação de Segurança',
        text: 'Complete a verificação antes de continuar.', confirmButtonColor: '#3b82f6' });
      return;
    }

    const payload = {
      subscription_id: paymentResult.subscription_id,
      person_type: personType,
      account_type: planConfig.account_type,
      usage_type: planConfig.usage_type,
      company_name: data.company_name.trim(),
      first_name: isPJ ? data.first_name.trim() : (data.company_name.trim().split(' ')[0] || 'Usuário'),
      terms_accepted: !!data.terms_accepted,
    };
    if (turnstileToken) payload.turnstile_token = turnstileToken;

    setProcessing(true);
    try {
      const res = await signupFinalize(payload);
      if (!res?.data?.success) throw new Error('Resposta inesperada do servidor');

      onRegisterSuccess({
        accountId: res.data.account?.id,
        signupToken: res.data.signup_token,
        userEmail: res.data.account?.email || paymentResult.customer_email,
        trialDays: 0,
      });
    } catch (err) {
      const errData = err?.response?.data || {};
      const errCode = errData.error_code;

      if (errCode === 'ALREADY_FINALIZED') {
        Swal.fire({
          icon: 'info', title: 'Cadastro já finalizado',
          text: 'Este pagamento já foi vinculado a uma conta. Faça login para acessar.',
          confirmButtonText: 'Ir para login', confirmButtonColor: '#3b82f6',
        }).then(() => { window.location.href = '/login'; });
      } else if (errCode === 'PAYMENT_NOT_CONFIRMED') {
        Swal.fire({
          icon: 'warning', title: 'Pagamento ainda não confirmado',
          text: 'Aguarde alguns instantes e tente novamente. Se persistir, contate o suporte.',
          confirmButtonColor: '#3b82f6',
        });
      } else if (errCode === 'ACCOUNT_ALREADY_EXISTS') {
        Swal.fire({
          icon: 'info', title: 'Conta já existe',
          text: 'Já existe uma conta com este e-mail. Faça login para acessar.',
          confirmButtonText: 'Ir para login', confirmButtonColor: '#3b82f6',
        }).then(() => { window.location.href = '/login'; });
      } else {
        let msg = 'Erro ao finalizar o cadastro. Contate o suporte se o problema persistir.';
        if (errData?.error) msg = errData.error;
        Swal.fire({ icon: 'error', title: 'Erro', text: msg, confirmButtonColor: '#3b82f6' });
        if (shouldShowTurnstile) {
          setTurnstileResetTrigger((p) => p + 1);
          setTurnstileToken(null);
        }
      }
    } finally {
      setProcessing(false);
    }
  };

  const formatBRL = (value) => {
    const n = Number(value || 0);
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);
  };

  return (
    <div className="signup-free-form">
      <div className="signup-free-form__card">
        <div className="signup-free-form__header">
          <div className={`signup-free-form__plan-badge signup-free-form__plan-badge--${planConfig.planBadgeVariant || 'success'}`}>
            <IconSparkles size={16} />
            Plano {planConfig.planName}
          </div>
          <p>Conclua seu cadastro para liberar o acesso à plataforma</p>
        </div>

        <Alert variant="success" className="d-flex align-items-center mb-4">
          <IconCircleCheck size={20} className="me-2 flex-shrink-0" />
          <div>
            <strong>Pagamento confirmado!</strong>{' '}
            {paymentResult?.value ? `${formatBRL(paymentResult.value)} cobrados.` : ''}
            {paymentResult?.next_due_date && (
              <span> Próximo vencimento em {new Date(paymentResult.next_due_date).toLocaleDateString('pt-BR')}.</span>
            )}
          </div>
        </Alert>

        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Dados confirmados (read-only) */}
          <div className="mb-4 p-3" style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <small className="text-muted d-block mb-2">Dados já confirmados no pagamento:</small>
            <div className="d-flex flex-column gap-1" style={{ fontSize: '0.875rem' }}>
              <div><strong>Nome:</strong> {paymentResult?.customer_name}</div>
              <div><strong>E-mail:</strong> {paymentResult?.customer_email}</div>
              <div><strong>Tipo:</strong> {isPJ ? 'Pessoa Jurídica' : 'Pessoa Física'}</div>
              {paymentResult?.card_last_digits && (
                <div><strong>Cartão:</strong> {paymentResult.card_brand || ''} ····{paymentResult.card_last_digits}</div>
              )}
            </div>
          </div>

          {/* Razão social / nome (sempre confirma o que foi digitado no pagamento, editável) */}
          <Form.Group className="mb-3">
            <Form.Label>
              <IconBuildingStore size={16} className="me-2" />
              {isPJ ? 'Nome da Empresa' : 'Nome Completo'}
            </Form.Label>
            <Form.Control type="text"
              placeholder={isPJ ? 'Ex: Minha Empresa LTDA' : 'Ex: João Silva'}
              {...register('company_name', {
                required: 'Obrigatório',
                maxLength: { value: 128, message: 'Máximo 128 caracteres' },
              })}
              disabled={processing} isInvalid={!!errors.company_name} />
            {errors.company_name && <Form.Control.Feedback type="invalid">{errors.company_name.message}</Form.Control.Feedback>}
          </Form.Group>

          {/* Nome do responsável só para PJ */}
          {isPJ && (
            <Form.Group className="mb-3">
              <Form.Label><IconUser size={16} className="me-2" />Nome do Responsável</Form.Label>
              <Form.Control type="text" placeholder="Ex: Maria Santos"
                {...register('first_name', {
                  required: 'Obrigatório',
                  maxLength: { value: 70, message: 'Máximo 70 caracteres' },
                })}
                disabled={processing} isInvalid={!!errors.first_name} />
              {errors.first_name && <Form.Control.Feedback type="invalid">{errors.first_name.message}</Form.Control.Feedback>}
            </Form.Group>
          )}

          {/* Termos */}
          <Form.Group className="mb-4">
            <Form.Check type="checkbox" id="terms-paid"
              label={
                <span style={{ fontSize: '0.8125rem' }}>
                  Li e concordo com os{' '}
                  <span className="signup-terms-link"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}>
                    Termos de Uso
                  </span>
                </span>
              }
              {...register('terms_accepted', { required: 'Você precisa aceitar os termos de uso' })}
              disabled={processing} isInvalid={!!errors.terms_accepted}
              feedback={errors.terms_accepted?.message} feedbackType="invalid" />
          </Form.Group>

          {shouldShowTurnstile && (
            <div className="mb-3">
              <TurnstileWidget
                onTokenChange={(t) => { setTurnstileToken(t); setTurnstileError(null); }}
                onError={(e) => { setTurnstileError(e); setTurnstileToken(null); }}
                onExpired={() => { setTurnstileToken(null); setTurnstileError('Token expirado, refaça.'); }}
                resetTrigger={turnstileResetTrigger} />
              {turnstileError && (
                <Alert variant="danger" className="mt-2"><IconShieldCheck size={16} className="me-2" />{turnstileError}</Alert>
              )}
            </div>
          )}

          <button type="submit"
            className="signup-plan-card__cta signup-plan-card__cta--success w-100"
            disabled={processing || (shouldShowTurnstile && !turnstileToken)}>
            {processing ? (
              <><Spinner animation="border" size="sm" className="me-2" />Finalizando cadastro...</>
            ) : (
              <><IconCheck size={20} className="me-2" />Concluir Cadastro</>
            )}
          </button>

          <div className="signup-free-form__secure">
            <IconShieldCheck size={14} />
            <span>Seus dados estão seguros conosco</span>
          </div>
        </Form>
      </div>

      <ModalComponent
        props={{
          title: 'Termos de Uso',
          content: <NewTermsOfUse />,
          showModal: showTermsModal,
          setShowModal: setShowTermsModal,
          size: 'xl',
        }}
      />
    </div>
  );
};

export default PaidRegisterForm;
